import { formatUrl, parseUrl } from '../instance/urls.ts';
/**
 * R3 — plain serializable models. No `Date`, no `URL`, no `Map`/`Set`, no classes. Explicit
 * `| null` rather than optional-and-absent, so the Kotlin port is mechanical.
 */
export interface ImageRef {
  /** Already rewritten to the instance (R2); never a Google host. */
  readonly url: string;
  readonly width: number | null;
  readonly height: number | null;
  /** Invidious's own label (`maxres`, `medium`, `start`, …) — passed through, not interpreted. */
  readonly quality: string | null;
}

export interface ImagePick {
  /** The **smallest** candidate at least this wide wins. See {@link pickImage} for why smallest. */
  readonly minWidth: number;
  /**
   * Prefer ~16:9 sources when the list offers both shapes. `videoThumbnails[]` mixes them:
   * `medium` (320×180) and `maxres` (1280×720) are the frame, while `sddefault` (640×480) and
   * `hqdefault` (480×360) are the same frame letterboxed into 4:3.
   */
  readonly widescreen?: boolean;
}

/** 16:10. Above it a thumbnail is the video's own frame; below it, a 4:3 box with bars. */
const WIDESCREEN_MIN_RATIO = 1.6;

function isWidescreen(image: ImageRef): boolean {
  return image.width !== null && image.height !== null && image.height > 0
    ? image.width / image.height >= WIDESCREEN_MIN_RATIO
    : false;
}

/**
 * Chooses one image out of an Invidious image array — an avatar for a byline, a thumbnail for a
 * listing row. Three screens each had a hand-rolled version of this, and the rule they
 * shared ("the first one at least N wide") is only correct for avatars, because
 * `authorThumbnails[]` happens to be sorted ascending (32 → 512) and `videoThumbnails[]` descending
 * (1280 → 120). Order-independent here, so the same call works on both.
 *
 * **Smallest sufficient, never biggest available**, for two measured reasons:
 *
 * - Bytes. A rail of 12 recommendations is 12 requests: `maxres` for the R12 fixture video is
 *   **266 KB**, `medium` is **13.5 KB**. At 168 px wide the extra pixels are thrown away by the
 *   scaler.
 * - The declared dimensions are the size *requested*, not the size served. Invidious answers
 *   `/vi/{id}/maxres.jpg` with a 200 for every video, falling back rather than 404ing:
 *   `jNQXAC9IVRw` declares `maxres: 1280×720` and serves **480×360**. `medium` was 320×180 on
 *   every video probed, old and new — the smallest sufficient pick is also the honest one.
 *
 * Returns `null` only for an empty list. A list whose entries carry no dimensions at all (a bare
 * `playlistThumbnail`, a comment's creator-heart avatar — see `parseImageUrl`) yields its first
 * entry: unknown size is not a reason to render nothing.
 */
export function pickImage(images: readonly ImageRef[], pick: ImagePick): ImageRef | null {
  if (images.length === 0) return null;

  const sized = images.filter((image) => image.width !== null && image.width > 0);
  if (sized.length === 0) return images[0] ?? null;

  const wide = sized.filter(isWidescreen);
  const candidates = pick.widescreen === true && wide.length > 0 ? wide : sized;

  // `<` and `>` rather than `<=`/`>=` so ties keep the API's own order — `maxres` and
  // `maxresdefault` are both declared 1280×720 and are the same picture.
  let smallestSufficient: ImageRef | null = null;
  let largest = candidates[0] ?? null;
  for (const image of candidates) {
    const width = image.width ?? 0;
    if (width >= pick.minWidth && (smallestSufficient === null || width < (smallestSufficient.width ?? 0))) {
      smallestSufficient = image;
    }
    if (largest !== null && width > (largest.width ?? 0)) largest = image;
  }

  // Nothing reaches `minWidth` → the largest there is, upscaled, rather than nothing at all.
  return smallestSufficient ?? largest;
}

/**
 * A sideloaded WebVTT caption track, resolved to the caption path that actually returns bytes
 * (§2 trap 1). `languageCode` normalises the `languageCode` / `language_code` split between the
 * two endpoints that carry it.
 */
export interface SubtitleTrackRef {
  readonly label: string;
  readonly languageCode: string;
  readonly url: string;
  /**
   * Heuristic: YouTube marks these in the *label* ("English (auto-generated)") and nowhere else,
   * so `SubtitlePref.allowAutoGenerated` is enforced against a string match. Documented as such
   * because a localised instance may label them differently.
   */
  readonly autoGenerated: boolean;
}

/**
 * §7 — scrub previews come from the finished WebVTT thumbnail track, not from sprite math.
 * `count` and `intervalMs` exist so the hover time can be clamped to
 * `min(duration, count × interval / 1000)`: the VTT over-emits trailing cues that point at blank
 * tiles.
 */
export interface StoryboardSpec {
  readonly vttUrl: string;
  readonly width: number | null;
  readonly height: number | null;
  readonly count: number | null;
  readonly intervalMs: number | null;
}

/**
 * One preview tile: a rectangle inside a sprite sheet, valid for a span of the timeline.
 *
 * `x`/`y`/`w`/`h` come from the cue URL's `#xywh=` fragment, which is why {@link StoryboardSpec}
 * exists separately — the fragment says where the tile is, the spec says how many tiles are real.
 * The platform renders this as a `background-position` crop (§7); core does no image work at all.
 */
export interface StoryboardCue {
  readonly startMs: number;
  readonly endMs: number;
  /** Transport-resolved (R2), with the `#xywh=` fragment removed — it is parsed into the fields below. */
  readonly imageUrl: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * The nine names Invidious/YouTube serve a video frame under, and the two of them worth asking for.
 *
 * `/vi/{videoId}/{name}.jpg` is addressable from a bare video id — §8's history hydration already
 * depends on that — which is what makes {@link retargetVideoFrameUrl} possible at all.
 */
export type VideoFrameName = 'mqdefault' | 'maxresdefault';

const VI_PATH_RE = /^(.*\/vi(?:_webp)?\/)([^/]+)\/([^/]+)$/;

/**
 * Rewrites a `/vi/{id}/{something}.jpg` URL to a different frame size — the {@link pickImage} problem
 * for an endpoint that hands over **one URL instead of a size list**.
 *
 * `playlistThumbnail` is that endpoint, and the numbers are why this function exists. Measured on
 * 2026-07-29, for the frame a search playlist row points at:
 *
 * ```
 * /vi/l5OZu-IrXpw/hq720.jpg?sqp=-oaymwEXCOgC…   200   275 088 bytes
 * /vi/l5OZu-IrXpw/hq720.jpg  (sqp removed)      200   275 088 bytes   ← the resize hint is inert
 * /vi/l5OZu-IrXpw/mqdefault.jpg                 200    16 929 bytes
 * ```
 *
 * The box it renders into is ~278 × 157 CSS pixels, so a 20-playlist results page was fetching about
 * **5.5 MB** where 340 KB is sufficient — sixteen times over, the same mistake 2b measured in
 * `pickImage(…, {minWidth: 480})` and unreachable by the same fix, because there is no list to pick
 * from. Note the second line: `sqp=` looks like a resize instruction and changes nothing, so the query
 * is dropped rather than carried (a bare `/vi/…` frame needs no signature — verified 200).
 *
 * **It returns the URL unchanged for anything that is not that shape.** `playlistThumbnail` has a
 * *second* form for YouTube's auto-generated mix playlists —
 * `https://i9.ytimg.com/s_p/{RDCLAK5uy_…}/mqdefault.jpg`, 2–8 of 20 rows depending on the query — whose
 * path segment is a **playlist** id rather than a video id, so rewriting it by pattern would build a
 * URL for a video that does not exist.
 *
 * In practice those never reach this function: `rewriteAssetUrl` whitelists `/vi/` and `/sb/` under the
 * ytimg hosts and refuses `/s_p/`, so the model already carries `thumbnail: null` and the card draws its
 * empty frame. That refusal is right rather than a gap — the URL is dead in **both** directions
 * (`i9.ytimg.com` answers **404**, and the instance answers **504** because it proxies no such path), so
 * a mix playlist genuinely has no obtainable image and there is no first-video id in the record to
 * derive one from. The arm here is defence in depth for a parser that might one day pass it through.
 */
export function retargetVideoFrameUrl(url: string, name: VideoFrameName): string {
  const parts = parseUrl(url);
  if (parts === null) return url;

  const match = VI_PATH_RE.exec(parts.path);
  if (match === null) return url;

  const [, prefix, id, file] = match;
  if (prefix === undefined || id === undefined || file === undefined) return url;
  // Only a real image file, and only when the id looks like one: `/s_p/…` never reaches here, but a
  // future third shape under `/vi/` should fall through rather than be guessed at.
  if (!/\.(?:jpg|jpeg|webp|png)$/i.test(file)) return url;

  const extension = /\.webp$/i.test(file) ? 'webp' : 'jpg';
  return formatUrl({ ...parts, path: `${prefix}${id}/${name}.${extension}`, query: '', fragment: '' });
}
